import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, FileText, Printer, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BillingLedger, invalidate } from '@/components/billing/Ledger';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { billingApi } from '@/lib/api/endpoints';
import type { Client, WorkReport } from '@/lib/api/types';
import { describeFailure } from '@/lib/i18n/errors';
import { useT } from '@/lib/i18n';
import { Page } from '@/routes/Today';

/**
 * Clients — the people whose clock tracked time runs on.
 *
 * Creating one is deliberately a single row: name and rate. Everything else
 * (IČO, address for the printed report) can wait until the first report needs
 * it — a scarier form here would mean clients never get created and hours
 * never get an owner.
 */
export function ClientsRoute() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['clients'],
    queryFn: () => billingApi.clients(),
  });
  const { data: unbilledOverview } = useQuery({
    queryKey: ['unbilled', 'overview'],
    queryFn: () => billingApi.unbilledOverview(),
  });
  const unbilledFor = (clientId: string) =>
    unbilledOverview?.find((row) => row.client_id === clientId);

  const [name, setName] = useState('');
  const [rate, setRate] = useState('');

  const create = useMutation({
    mutationFn: () =>
      billingApi.createClient({ name, hourly_rate: rate.replace(',', '.') || '0' }),
    onSuccess: () => {
      setName('');
      setRate('');
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );

  return (
    <Page>
      <h1 className="mb-1 text-xl font-semibold tracking-tight">{t('clients.title')}</h1>
      {/* The dashboard line: what is earned and not yet billed, anywhere. */}
      <p className="mb-6 text-sm text-content-subtle">
        {unbilledOverview && unbilledOverview.length > 0
          ? t('clients.unbilledTotal', {
              amount: unbilledOverview
                .map((row) => formatAmount(Number(row.amount), row.currency))
                .join(' + '),
            })
          : t('clients.allBilled')}
      </p>

      <form
        className="mb-6 flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('clients.namePlaceholder')}
          className="min-w-40 flex-[2] rounded-lg bg-surface px-3 py-2 text-sm ring-1 ring-border focus:outline-none focus:ring-accent text-content placeholder:text-content-subtle"
        />
        <input
          value={rate}
          onChange={(event) => setRate(event.target.value.replace(/[^0-9.,]/g, ''))}
          placeholder={t('clients.ratePlaceholder')}
          inputMode="decimal"
          className="w-32 rounded-lg bg-surface px-3 py-2 text-sm ring-1 ring-border focus:outline-none focus:ring-accent text-content placeholder:text-content-subtle"
        />
        <Button type="submit" size="sm" disabled={!name.trim() || create.isPending}>
          {t('clients.add')}
        </Button>
      </form>

      {data && data.length === 0 ? (
        <EmptyState
          icon={<FileText className="h-5 w-5" />}
          title={t('clients.empty.title')}
          hint={t('clients.empty.hint')}
        />
      ) : (
        <ul className="divide-y divide-border rounded-xl bg-surface ring-1 ring-border text-content placeholder:text-content-subtle">
          {(data ?? []).map((client) => (
            <li key={client.id}>
              <Link
                to={`/clients/${client.id}`}
                className="flex items-center gap-3 px-4 py-3 transition-surface hover:bg-surface-hover"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-xs font-semibold text-accent">
                  {initials(client.name)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {client.name}
                </span>
                {unbilledFor(client.id) ? (
                  <span className="rounded-full bg-accent-subtle px-2 py-0.5 text-xs font-medium tabular-nums text-accent">
                    {formatMinutes(unbilledFor(client.id)!.minutes)} ·{' '}
                    {formatAmount(
                      Number(unbilledFor(client.id)!.amount),
                      unbilledFor(client.id)!.currency,
                    )}
                  </span>
                ) : null}
                <span className="text-xs tabular-nums text-content-subtle">
                  {formatRate(client)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
}

/**
 * One client: the unbilled period, the button that freezes it into a report,
 * and the reports already issued.
 */
export function ClientDetailRoute() {
  const t = useT();
  const toast = useToast();
  const { clientId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: clients } = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => billingApi.clients(true),
  });
  const client = clients?.find((row) => row.id === clientId);

  const { data: reports } = useQuery({
    queryKey: ['reports', clientId],
    queryFn: () => billingApi.reports(clientId),
    enabled: Boolean(clientId),
  });

  // Issuing bills exactly what was ticked, over exactly the days those items
  // cover — no period picker, because the selection already says both.
  const issue = useMutation({
    mutationFn: (selection: {
      entry_ids: string[];
      task_ids: string[];
      period_start: string;
      period_end: string;
    }) =>
      billingApi.issueReport(clientId, selection.period_start, selection.period_end, {
        entry_ids: selection.entry_ids,
        task_ids: selection.task_ids,
      }),
    onSuccess: (report) => {
      invalidate(queryClient);
      navigate(`/reports/${report.id}/print`);
    },
    onError: (failure) => toast.error(describeFailure(failure, 'common.error')),
  });

  const archive = useMutation({
    mutationFn: () => billingApi.updateClient(clientId, { archived: !client?.archived_at }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });

  const rate = useMutation({
    mutationFn: (patch: Partial<Client>) => billingApi.updateClient(clientId, patch),
    onSuccess: () => {
      toast.show(t('common.saved'));
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  if (!client) return null;

  return (
    <Page>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{client.name}</h1>
          <p className="mt-0.5 text-sm text-content-subtle">
            {formatRate(client)}
            {client.archived_at ? ` · ${t('clients.archived')}` : ''}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => archive.mutate()}>
          <Archive className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {client.archived_at ? t('clients.unarchive') : t('clients.archive')}
        </Button>
      </header>

      <section className="mb-8 rounded-xl bg-surface p-4 ring-1 ring-border text-content placeholder:text-content-subtle">
        <h2 className="mb-3 text-sm font-semibold">{t('clients.billing')}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <LabeledInput
            label={t('clients.rate')}
            defaultValue={client.hourly_rate}
            onSave={(value) => rate.mutate({ hourly_rate: value.replace(',', '.') })}
          />
          <LabeledInput
            label={t('clients.company')}
            defaultValue={client.company ?? ''}
            onSave={(value) => rate.mutate({ company: value })}
          />
          <LabeledInput
            label={t('clients.registrationNo')}
            defaultValue={client.registration_no ?? ''}
            onSave={(value) => rate.mutate({ registration_no: value })}
          />
          <LabeledInput
            label={t('clients.address')}
            defaultValue={client.address ?? ''}
            onSave={(value) => rate.mutate({ address: value })}
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-1 text-sm font-semibold">{t('ledger.title')}</h2>
        <p className="mb-3 text-xs text-content-subtle">{t('ledger.hint')}</p>
        <BillingLedger
          clientId={clientId}
          currency={client.currency}
          onIssue={(selection) => issue.mutate(selection)}
        />
      </section>

      {reports && reports.length > 0 ? (
        <section>
          <h2 className="mb-3 text-sm font-semibold">{t('clients.reports')}</h2>
          <ul className="divide-y divide-border rounded-xl bg-surface ring-1 ring-border text-content placeholder:text-content-subtle">
            {reports.map((report) => (
              <ReportRow key={report.id} report={report} />
            ))}
          </ul>
        </section>
      ) : null}
    </Page>
  );
}

function ReportRow({ report }: { report: WorkReport }) {
  const t = useT();
  const queryClient = useQueryClient();
  const voidReport = useMutation({
    mutationFn: () => billingApi.voidReport(report.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      void queryClient.invalidateQueries({ queryKey: ['unbilled'] });
      void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
    },
  });
  const markPaid = useMutation({
    mutationFn: (paid: boolean) => billingApi.markReportPaid(report.id, paid),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['reports'] }),
  });
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 text-sm">
      {/* Paid is the one fact an issued report may still record. Unpaid is
          the state worth noticing, so it is the checkbox that stands out. */}
      <label
        className="flex cursor-pointer items-center gap-1.5"
        title={report.paid_at ? t('report.paidOn', { date: day(report.paid_at) }) : t('report.markPaid')}
      >
        <input
          type="checkbox"
          checked={Boolean(report.paid_at)}
          onChange={(event) => markPaid.mutate(event.target.checked)}
          className="h-3.5 w-3.5 accent-[oklch(52.6%_0.096_150)]"
        />
      </label>
      <span className="font-medium tabular-nums">{report.number}</span>
      <span className="min-w-0 flex-1 truncate text-content-subtle">
        {formatDay(report.period_start)} – {formatDay(report.period_end)}
      </span>
      <span className="tabular-nums text-content-subtle">
        {formatMinutes(report.total_minutes)}
      </span>
      <span
        className={
          report.paid_at
            ? 'font-medium tabular-nums text-content-subtle'
            : 'font-medium tabular-nums text-priority-high'
        }
        title={report.paid_at ? t('report.paid') : t('report.unpaid')}
      >
        {formatAmount(Number(report.total_amount), report.currency)}
      </span>
      <Link
        to={`/reports/${report.id}/print`}
        className="text-accent hover:underline"
        title={t('clients.print')}
      >
        <Printer className="h-4 w-4" aria-hidden />
      </Link>
      <button
        type="button"
        title={t('report.void')}
        aria-label={t('report.void')}
        onClick={() => {
          // A native confirm, because voiding withdraws a numbered document.
          if (window.confirm(t('report.voidConfirm', { number: report.number }))) {
            voidReport.mutate();
          }
        }}
        className="rounded p-0.5 text-content-subtle transition-surface hover:text-danger"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </button>
    </li>
  );
}

function LabeledInput({
  label,
  defaultValue,
  onSave,
}: {
  label: string;
  defaultValue: string;
  onSave: (value: string) => void;
}) {
  return (
    <label className="block text-xs text-content-subtle">
      {label}
      <input
        defaultValue={defaultValue}
        onBlur={(event) => {
          if (event.target.value !== defaultValue) onSave(event.target.value);
        }}
        className="mt-1 w-full rounded-lg bg-canvas px-3 py-2 text-sm text-content ring-1 ring-border focus:outline-none focus:ring-accent"
      />
    </label>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => /\p{L}|\p{N}/u.test(word))
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}


export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')} min`;
}

export function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatRate(client: Client): string {
  return `${formatAmount(Number(client.hourly_rate), client.currency)}/h`;
}

function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day}. ${month}. ${year}`;
}

function day(isoTimestamp: string): string {
  return formatDay(isoTimestamp.slice(0, 10));
}
